/**
 * TeamManagementView — the Team Roster page: merged Employees/opsbee Users
 * grid (one dataset, two column projections), Status/Role filters, Issues
 * (drift) diagnostics, column visibility, sort, Print, Excel export, the
 * Importer Connection status badge, the row-click Employee Profile modal, and
 * the toolbar "+ Add" button opening AddEmployeeModal (868kadxqh).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { HStack, Spinner, VStack, View, WarningIcon } from 'native-base';

import { Button, MsExcel, PrinterIcon, Text, UserRoundPlusIcon } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { useAuthPermissions, useAuthToken } from '@app/features/app';
import { useSiteConfig, useSiteTime } from '@app/features/store-core';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { SidebarMenu } from './sidebar-menu';
import { useSidebarMenu } from '../hooks/sidebar-menu.hook';
import {
  COL_EMPLOYMENT_STATUS_USERS,
  COL_IS_PRIMARY,
  COL_SYNC_MODE,
  TEAM_COLUMNS,
  USERS_COLUMNS,
  applySorts,
  defaultVisibleIdsForScope,
  type ColumnContext,
  type FilterScope,
  type SortKey,
} from './team-management-columns';
import { useGetTeamMembers } from '../hooks/get-team-members.hook';
import { useCreateTeamMember } from '../hooks/create-team-member.hook';
import {
  TEAM_DRIFT_KINDS,
  USERS_DRIFT_KINDS,
  type DriftKind,
  type RoleFilterValue,
  type StatusFilterValue,
  type TeamMember,
  type TeamMemberRole,
  type TeamMemberStatus,
  type UserLoginStatus,
  type ViewMode,
} from '../types';
import type { ImportSyncModeEnum } from '@pimm/services/lib/sms-workforce/types';
import { toIsoBirthDate, toIsoDate } from '../utils';
import { ColumnsPopover } from './team-management-columns-popover';
import { ExportButton, ExportScope } from './team-management-export-button';
import { ImporterConnection } from './team-management-importer-connection';
import { KeyHelpButton } from './team-management-key-help-button';
import { MemberProfileModal } from './team-management-member-profile-modal';
import { AddEmployeeModal } from './team-management-add-employee-modal';
import { MembersTable } from './team-management-members-table';
import { PrintView } from './team-management-print-view';
import { RoleFilter } from './team-management-role-filter';
import { StatusFilter } from './team-management-status-filter';
import { ViewSelector } from './team-management-view-selector';
import { matchesDrift } from './team-management-table-shared';
import { buildExportFileName, buildReportInfoRows, formatReportAddress } from './team-management-report-chrome';
import { useExportTo } from '@app/features/store-equipment/hooks/export-to.hook';

export type TeamManagementViewProps = {
  initialPageModule?: string;
  initialPageSubModule?: string;
  onPageChange?: (pageModule?: string, pageSubModule?: string) => void;
};

// Maps the StatusFilterValue union to a FilterScope string for column visibility.
const filterToScope = (f: StatusFilterValue): FilterScope =>
  typeof f === 'object' && f.kind === 'drift' ? (`drift:${f.drift}` as FilterScope) : f === 'Active' ? 'active' : 'notActive';

// Empty filter set = "no filter" (show every role). Non-empty narrows to only the roles in the set.
const roleMatchesFilter = (role: TeamMemberRole, filter: RoleFilterValue): boolean => (filter.size === 0 ? true : filter.has(role));

// `drift_${slug}` dynamic i18n key pattern — keep this as a template, not a
// static switch, so adding a new drift kind later doesn't require a new
// `t()` call site.
const driftSlug = (kind: DriftKind): string => kind.replace(/[A-Z]/g, l => `_${l.toLowerCase()}`);

export const TeamManagementView = ({ initialPageModule, initialPageSubModule, onPageChange }: TeamManagementViewProps) => {
  const { t } = useTranslation();
  const { locale } = useAppLocale();
  const { siteConfig } = useSiteConfig();
  const { user } = useAuthToken();
  const { xlsxExport } = useExportTo();
  // Site's UTC offset — Last Login/Last Scheduled/Status Updated "Today" vs
  // "Yesterday" bucketing (and the 30-day drift thresholds) track the store's
  // clock, not the viewing device's. Same pattern as ImporterConnection's
  // site-local sync timestamp.
  const siteTime = useSiteTime();
  const utcOffsetInMinutes = siteTime.utcOffsetInMinutes ?? 0;

  const permissions = useAuthPermissions(s => s.permissions);
  const canEditRoster = !!permissions?.storeSettingsTeamMgmtTeamRoster?.write;
  // Own dedicated claim, not the general teamRoster.edit — and it doesn't end
  // in view/edit, so useAuthPermissions never computes a meaningful `.write`
  // for it (see auth-permissions.context.tsx). Presence of the key is the
  // correct signal, same as userSettingsInfo/appModule*PinRestricted elsewhere.
  const canSetPrimary = !!permissions?.storeSettingsTeamMgmtTeamRosterSetPrimary;

  // Single item ("Team Roster") — matches the left-pane shell used by Store
  // Configuration's General Info/Store Hours/Equipment Profile for visual
  // consistency, even though there's only one page to navigate to. The
  // Employees/opsbee Users split is a separate concern, driven by the
  // ViewSelector tab bar below (not by sidebar navigation).
  const sidebar = useSidebarMenu('teamMgmt', { initialPageModule, initialPageSubModule, onPageChange });

  const [teamQuery, , updateCachedMember, insertCachedMember] = useGetTeamMembers({ query: { siteId: siteConfig?.id } });
  const rawMembers: TeamMember[] = teamQuery.data?.teamMembers ?? [];
  const syncInfo = teamQuery.data?.syncInfo;

  const createTeamMember = useCreateTeamMember();
  const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState(false);

  // Filter + view state. Each view has its own status-filter state — Team and
  // Users surface different concerns (employment vs login health) so the
  // Active/NotActive predicates AND the Issues popover's drift kinds differ.
  // Switching views preserves each tab's filter selection.
  const [teamStatusFilter, setTeamStatusFilter] = useState<StatusFilterValue>('Active');
  const [usersStatusFilter, setUsersStatusFilter] = useState<StatusFilterValue>('Active');
  const [roleFilter, setRoleFilter] = useState<RoleFilterValue>(() => new Set());

  // The ViewSelector tab bar (Employees / opsbee Users) rendered below the
  // title row is the single source of truth for which view is active —
  // independent of the left-pane sidebar, which only navigates between
  // Team Management's pages (today, just "Team Roster").
  const [viewMode, setViewMode] = useState<ViewMode>('team');

  const statusFilter = viewMode === 'users' ? usersStatusFilter : teamStatusFilter;
  const setStatusFilter = viewMode === 'users' ? setUsersStatusFilter : setTeamStatusFilter;

  // Sort + visibility — per view, kept in view state. Visibility resets to the
  // active filter scope's defaults whenever the filter changes (Active / Not
  // Active / drift kind) so the displayed columns track the manager's intent
  // automatically.
  const [teamSortKeys, setTeamSortKeys] = useState<SortKey[]>([]);
  const [usersSortKeys, setUsersSortKeys] = useState<SortKey[]>([]);
  const [teamVisible, setTeamVisible] = useState<string[]>(() => defaultVisibleIdsForScope(TEAM_COLUMNS, filterToScope('Active')));
  const [usersVisible, setUsersVisible] = useState<string[]>(() => defaultVisibleIdsForScope(USERS_COLUMNS, filterToScope('Active')));

  // Track filter scope to detect changes; reset visibility on transition.
  const currentScope = filterToScope(statusFilter);
  useEffect(() => {
    setTeamVisible(defaultVisibleIdsForScope(TEAM_COLUMNS, currentScope));
    setUsersVisible(defaultVisibleIdsForScope(USERS_COLUMNS, currentScope));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScope]);

  // Print modal state
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [printScope, setPrintScope] = useState<ExportScope>('current');

  // Employee Profile modal state — opened by a row click (gated on
  // canEditRoster, see MembersTable's onRowPress below). Re-resolved against
  // rawMembers by id (not the stale click-time snapshot) so the modal shows
  // fresh data if teamQuery refetches while it's open.
  const memberProfileModal = useModalFocus<TeamMember>();
  const activeProfileMember = memberProfileModal.isOpen
    ? rawMembers.find(m => m.id === memberProfileModal.payload?.id) ?? memberProfileModal.payload ?? null
    : null;

  // Roles present in the current view's dataset — Team uses Employee.role,
  // Users uses User.userRole. The role filter popover renders only these so
  // picking a role that yields zero rows is never offered.
  const availableRoles = useMemo<Set<TeamMemberRole>>(() => {
    const set = new Set<TeamMemberRole>();
    for (const m of rawMembers) {
      if (viewMode === 'users') {
        if (m.user) set.add(m.user.userRole);
      } else {
        set.add(m.role);
      }
    }
    return set;
  }, [rawMembers, viewMode]);

  // Drop any previously-selected roles that aren't available in the new view
  // (e.g. user picked Crew on Team, then switched to Users where no Crew
  // logins exist). Keeps the chip count honest.
  useEffect(() => {
    setRoleFilter(prev => {
      let mutated = false;
      const next = new Set<TeamMemberRole>();
      for (const r of prev) {
        if (availableRoles.has(r)) next.add(r);
        else mutated = true;
      }
      return mutated ? next : prev;
    });
  }, [availableRoles]);

  // Counts derived from the relevant population per view:
  //   - Team view: every member (employment lifecycle)
  //   - Users view: only members with a user record (login health)
  // Counts are role-aware: when a role filter is active the chips (Active /
  // Not Active / Issues) reflect only the members matching that filter.
  const teamCounts = useMemo(() => {
    let active = 0;
    let notActive = 0;
    const drift: Partial<Record<DriftKind, number>> = {};
    for (const k of TEAM_DRIFT_KINDS) drift[k] = 0;
    for (const m of rawMembers) {
      if (!roleMatchesFilter(m.role, roleFilter)) continue;
      if (m.status === 'Active') active++;
      else notActive++;
      for (const k of TEAM_DRIFT_KINDS) {
        if (matchesDrift(m, k, utcOffsetInMinutes)) drift[k] = (drift[k] ?? 0) + 1;
      }
    }
    return { active, notActive, drift };
  }, [rawMembers, roleFilter, utcOffsetInMinutes]);

  const usersCounts = useMemo(() => {
    let active = 0;
    let notActive = 0;
    const drift: Partial<Record<DriftKind, number>> = {};
    for (const k of USERS_DRIFT_KINDS) drift[k] = 0;
    for (const m of rawMembers) {
      if (!m.user) continue;
      if (!roleMatchesFilter(m.user.userRole, roleFilter)) continue;
      if (m.user.status === 'Active') active++;
      else notActive++;
      for (const k of USERS_DRIFT_KINDS) {
        if (matchesDrift(m, k, utcOffsetInMinutes)) drift[k] = (drift[k] ?? 0) + 1;
      }
    }
    return { active, notActive, drift };
  }, [rawMembers, roleFilter, utcOffsetInMinutes]);

  const statusCounts = viewMode === 'users' ? usersCounts : teamCounts;

  // Localized labels
  // Three-way badge labels for the Employment Status cell.
  const statusLabels: Record<TeamMemberStatus, string> = {
    Active: t('store_settings:active'),
    Inactive: t('store_settings:inactive'),
    Terminated: t('store_settings:terminated'),
  };
  const roleLabels: Record<TeamMemberRole, string> = {
    'SMS MANAGER': t('store_settings:role_label_sms_manager'),
    'SMS USER': t('store_settings:role_label_sms_user'),
    'SMS EMPLOYEE': t('store_settings:role_label_sms_employee'),
  };
  const roleFilterAllLabel = t('store_settings:role_filter_all');

  const userStatusLabels: Record<UserLoginStatus, string> = {
    Active: t('store_settings:active'),
    Disabled: t('store_settings:user_status_disabled'),
    Pending: t('store_settings:user_status_pending'),
  };
  const columnContext: ColumnContext = {
    statusLabels,
    roleLabels,
    userStatusLabels,
    yesLabel: t('store_settings:yes'),
    noLabel: t('store_settings:no'),
    loginBadgeLabel: t('store_settings:has_login'),
    autoSyncOnLabel: t('store_settings:auto_sync_on'),
    autoSyncProtectedLabel: t('store_settings:auto_sync_protected'),
    autoSyncOffLabel: t('store_settings:auto_sync_off'),
    autoSyncPrefixLabel: t('store_settings:auto_sync'),
    primaryLabel: t('store_settings:primary'),
    emptyValue: '—',
    translate: t,
    utcOffsetInMinutes,
  };
  const resolveLabel = (key: string) => t(key);

  // Active view config
  const activeColumns = viewMode === 'users' ? USERS_COLUMNS : TEAM_COLUMNS;
  const activeSortKeys = viewMode === 'users' ? usersSortKeys : teamSortKeys;
  const setActiveSortKeys = viewMode === 'users' ? setUsersSortKeys : setTeamSortKeys;
  const activeVisible = viewMode === 'users' ? usersVisible : teamVisible;
  const setActiveVisible = viewMode === 'users' ? setUsersVisible : setTeamVisible;
  const minTableWidth = viewMode === 'users' ? 1180 : 1380;

  // Filtered + sorted members for the current view (used by the table and by
  // print/excel "Current view" scope).
  //
  // Active/NotActive predicates are view-aware:
  //   - Team view → m.status (Employee record lifecycle)
  //   - Users view → m.user.status (User login record state)
  // Drift filters apply the same matchesDrift() predicate regardless of view;
  // the available drift kinds per view (TEAM_DRIFT_KINDS / USERS_DRIFT_KINDS)
  // control what's in the Issues popover.
  const visibleMembers = useMemo<TeamMember[]>(() => {
    const isDriftFilter = typeof statusFilter === 'object' && statusFilter.kind === 'drift';
    const filtered = rawMembers
      .filter(m => (viewMode === 'users' ? m.userId != null : true))
      .filter(m => {
        if (isDriftFilter) return matchesDrift(m, (statusFilter as { drift: DriftKind }).drift, utcOffsetInMinutes);
        if (viewMode === 'users') {
          if (!m.user) return false;
          return statusFilter === 'Active' ? m.user.status === 'Active' : m.user.status !== 'Active';
        }
        return statusFilter === 'Active' ? m.status === 'Active' : m.status !== 'Active';
      })
      // Roles filter is sourced from the User record on Users view and from
      // the Employee record on Team view — matches the at-view role display
      // so filter results match what the manager sees in the table.
      .filter(m => {
        const memberRole = viewMode === 'users' ? m.user?.userRole ?? m.role : m.role;
        return roleMatchesFilter(memberRole, roleFilter);
      });
    return applySorts(filtered, activeSortKeys, activeColumns);
  }, [rawMembers, statusFilter, roleFilter, viewMode, activeSortKeys, activeColumns, utcOffsetInMinutes]);

  // Full-report dataset — all members in scope for the view (Users: still
  // filters out non-login members; Team: every row). Ignores Status/Role
  // filters and column hides.
  const fullMembers = useMemo<TeamMember[]>(() => {
    const base = viewMode === 'users' ? rawMembers.filter(m => m.userId != null) : rawMembers;
    return applySorts(base, [], activeColumns);
  }, [rawMembers, viewMode, activeColumns]);

  // --- Print + Excel helpers ---
  const exportReportTitle = () => (viewMode === 'users' ? t('store_settings:report_user_access') : t('store_settings:team_members'));

  // Active status/role filter, formatted for the report header + Excel info sheet.
  const statusFilterDescription = (() => {
    if (typeof statusFilter === 'object' && statusFilter.kind === 'drift') {
      return `${t('store_settings:status_issues')}: ${t(`store_settings:drift_${driftSlug(statusFilter.drift)}`)}`;
    }
    return statusFilter === 'Active' ? t('store_settings:active') : t('store_settings:status_not_active');
  })();
  const roleFilterText =
    roleFilter.size === 0
      ? roleFilterAllLabel
      : Array.from(roleFilter)
          .map(r => roleLabels[r])
          .join(', ');
  const currentFiltersText = `${statusFilterDescription} · ${roleFilterText}`;
  const viewLabel = viewMode === 'users' ? t('store_settings:view_users') : t('store_settings:view_team');

  // Filename rule (shared, matches Equipment): {Store}_{Report}_{FilterType}_{YYYY-MM-DD}.
  // "Current view" uses the active status filter; "Full report" always says
  // "Full Report" since that scope ignores the filter entirely. Same
  // filter-type text is used for both the Excel filename and the
  // print-to-PDF filename (via printWithFileName) — one convention, two artifacts.
  const exportFileName = (scope: ExportScope) =>
    buildExportFileName(
      siteConfig?.name,
      exportReportTitle(),
      new Date(),
      scope === 'current' ? statusFilterDescription : t('store_settings:print_full_report'),
    );

  // Site-level vendor name for the Employee Profile modal's Back Office
  // Vendor field — same value for every member (not a per-row grid column).
  const vendorName = syncInfo?.vendorInfo?.fullName;

  const brandLogoUrl = siteConfig?.companyInfo?.brand?.logoImgUrl;
  const chainName = siteConfig?.companyInfo?.chainName ?? '';
  const customerName = siteConfig?.companyInfo?.customerName ?? '';
  const address = formatReportAddress(siteConfig?.address);
  const generatedByName = user?.username ?? '';

  const generatedAtText = () => moment().locale(locale).format('MMM D, YYYY h:mm A');

  const handlePrint = (scope: ExportScope) => {
    setPrintScope(scope);
    setIsPrintOpen(true);
  };

  const handleExcel = (scope: ExportScope) => {
    const rows = scope === 'current' ? visibleMembers : fullMembers;
    const cols = scope === 'current' ? activeColumns.filter(c => activeVisible.includes(c.id) || c.alwaysVisible) : activeColumns;
    // Excel-only supplemental columns — intentionally NOT part of
    // TEAM_COLUMNS/USERS_COLUMNS (the on-screen grid and Print keep the
    // combined composite badge+icon cell exactly as-is). Always appended
    // regardless of scope or Columns-popover state, since there's no toggle
    // for them — Excel always gets full granularity.
    const excelOnlyCols =
      viewMode === 'users' ? [COL_EMPLOYMENT_STATUS_USERS, COL_SYNC_MODE, COL_IS_PRIMARY] : [COL_SYNC_MODE, COL_IS_PRIMARY];
    const jsonData = rows.map(m =>
      [...cols, ...excelOnlyCols].reduce<Record<string, string>>((acc, c) => {
        acc[resolveLabel(c.labelKey)] = c.renderText(m, columnContext);
        return acc;
      }, {}),
    );
    // Info sheet first (Store / View / Filters / Rows / Generated By·At), then
    // data — mirrors the Equipment Excel export's metadata sheet.
    const infoRows = buildReportInfoRows(
      {
        store: siteConfig?.name,
        view: viewLabel,
        filters: scope === 'current' ? currentFiltersText : t('store_settings:print_full_report'),
        rows: rows.length,
        generatedBy: generatedByName,
        generatedAt: generatedAtText(),
      },
      {
        store: t('store_settings:report_store'),
        view: t('store_settings:report_view'),
        filters: t('store_settings:report_filters'),
        rows: t('store_settings:report_rows'),
        generatedBy: t('store_settings:report_generated_by'),
        generatedAt: t('store_settings:report_generated_at'),
      },
    );
    xlsxExport(exportFileName(scope), [
      { sheet: 'Info', jsonData: infoRows },
      { sheet: viewMode === 'users' ? 'Users' : 'Team', title: exportReportTitle(), jsonData },
    ]);
  };

  // Assemble the dataset + columns passed to PrintView based on the chosen scope.
  const printColumns =
    printScope === 'current' ? activeColumns.filter(c => activeVisible.includes(c.id) || c.alwaysVisible) : activeColumns;
  const printMembers = printScope === 'current' ? visibleMembers : fullMembers;
  const teamMembersCountText = t(
    printMembers.length === 1 ? 'store_settings:team_members_count_one' : 'store_settings:team_members_count_other',
    { count: printMembers.length },
  );
  const printFilterDescription =
    printScope === 'current'
      ? `${currentFiltersText} · ${teamMembersCountText}`
      : `${t('store_settings:print_full_report')} · ${teamMembersCountText}`;

  const isLoading = teamQuery.isLoading;
  const isError = teamQuery.isError;

  return (
    <HStack flex={1}>
      <SidebarMenu {...sidebar} setActiveItem={sidebar.setActiveItem} />
      <View flex={1}>
        <VStack flex={1} overflow="hidden">
          {/* Page header — title + caption only. */}
          <HStack
            alignItems="center"
            justifyContent="space-between"
            px={4}
            py={3}
            bg="white"
            borderBottomWidth={1}
            borderColor="gray.200"
            flexShrink={0}
            flexWrap="wrap"
            space={3}
          >
            <VStack space={0.5}>
              <Text size="2xl" fontWeight={700} color="black">
                {t('store_settings:team_members')}
              </Text>
              <Text size="xs" color="gray.500">
                {t('store_settings:team_members_caption')}
              </Text>
            </VStack>
            {!!syncInfo && <ImporterConnection syncInfo={syncInfo} />}
          </HStack>

          {isLoading ? (
            <VStack flex={1} bg="grayCool.100" alignItems="center" justifyContent="center" space={3}>
              <Spinner size="lg" color="primary.500" />
              <Text size="sm" color="gray.500">
                {t('store_settings:loading_team_members')}
              </Text>
            </VStack>
          ) : isError ? (
            <VStack flex={1} bg="grayCool.100" alignItems="center" justifyContent="center" space={3} px={8}>
              <WarningIcon size={8} color="error.500" />
              <Text size="md" fontWeight={600} color="gray.900">
                {t('store_settings:couldnt_load_team_members')}
              </Text>
              <Text size="sm" color="gray.500" textAlign="center">
                {t('store_settings:try_refreshing')}
              </Text>
            </VStack>
          ) : (
            <VStack flex={1} bg="grayCool.100" px={4} py={3} space={3} overflow="hidden">
              <HStack>
                <ViewSelector
                  value={viewMode}
                  onChange={setViewMode}
                  teamLabel={t('store_settings:view_team')}
                  usersLabel={t('store_settings:view_users')}
                />
              </HStack>
              {/* Filter band rendered inside the card via topBand. Filters left,
              Columns + Print + Excel right. */}
              <MembersTable
                members={visibleMembers}
                columns={activeColumns}
                visibleColumnIds={activeVisible}
                sortKeys={activeSortKeys}
                onSortChange={setActiveSortKeys}
                columnContext={columnContext}
                resolveLabel={resolveLabel}
                minWidth={minTableWidth}
                emptyMessage={t('store_settings:team_members_empty')}
                onRowPress={canEditRoster ? member => memberProfileModal.setOpen(member) : undefined}
                maskConfidential={!canEditRoster}
                topBand={
                  <HStack
                    alignItems="center"
                    justifyContent="space-between"
                    px={4}
                    py={3}
                    bg="white"
                    borderBottomWidth={1}
                    borderColor="gray.200"
                    flexWrap="wrap"
                    space={3}
                  >
                    <HStack space={3} alignItems="center" flexWrap="wrap">
                      <StatusFilter
                        value={statusFilter}
                        onChange={setStatusFilter}
                        counts={statusCounts}
                        labels={{
                          active: t('store_settings:active'),
                          notActive: t('store_settings:status_not_active'),
                          issues: t('store_settings:status_issues'),
                        }}
                        driftKinds={viewMode === 'users' ? USERS_DRIFT_KINDS : TEAM_DRIFT_KINDS}
                        driftLabel={kind => t(`store_settings:drift_${driftSlug(kind)}`)}
                      />
                      {/* Status icon key — explains the sync/login/primary icons
                      packed into the status cell, since they carry meaning by
                      color alone. */}
                      <KeyHelpButton
                        viewMode={viewMode}
                        buttonLabel={t('store_settings:status_icon_key_accessibility_label')}
                        labels={{
                          loginStatusSectionTitle: t('store_settings:key_help_login_status_title'),
                          loginActiveTitle: t('store_settings:active'),
                          loginActiveDescription: t('store_settings:key_help_login_active_description'),
                          loginPendingTitle: t('store_settings:user_status_pending'),
                          loginPendingDescription: t('store_settings:key_help_login_pending_description'),
                          loginDisabledTitle: t('store_settings:user_status_disabled'),
                          loginDisabledDescription: t('store_settings:key_help_login_disabled_description'),
                          employeeStatusSectionTitle: t('store_settings:employment_status'),
                          employeeActiveTitle: t('store_settings:active'),
                          employeeActiveDescription: t('store_settings:key_help_employee_active_description'),
                          employeeInactiveTitle: t('store_settings:inactive'),
                          employeeInactiveDescription: t('store_settings:key_help_employee_inactive_description'),
                          employeeTerminatedTitle: t('store_settings:terminated'),
                          employeeTerminatedDescription: t('store_settings:key_help_employee_terminated_description'),
                          indicatorsSectionTitle: t('store_settings:key_help_indicators_title'),
                          importerStatusSectionTitle: t('store_settings:key_help_importer_status_title'),
                          primaryManagerTitle: t('store_settings:key_help_primary_manager_title'),
                          primaryManagerDescription: t('store_settings:key_help_primary_manager_description'),
                          autoSyncOnTitle: t('store_settings:auto_sync_on'),
                          autoSyncOnDescription: t('store_settings:key_help_auto_sync_on_description'),
                          autoSyncProtectedTitle: t('store_settings:auto_sync_protected'),
                          autoSyncProtectedDescription: t('store_settings:key_help_auto_sync_protected_description'),
                          autoSyncOffTitle: t('store_settings:auto_sync_off'),
                          autoSyncOffDescription: t('store_settings:key_help_auto_sync_off_description'),
                        }}
                      />
                    </HStack>
                    <HStack space={2} alignItems="center">
                      {canEditRoster && (
                        <Button
                          size="sm"
                          minH={9}
                          leftIcon={<UserRoundPlusIcon size={4} color="black" />}
                          onPress={() => setIsAddEmployeeOpen(true)}
                        >
                          {t('store_settings:add_employee_button_label')}
                        </Button>
                      )}
                      <RoleFilter
                        value={roleFilter}
                        onChange={setRoleFilter}
                        label={t('store_settings:filter_by_role')}
                        roleLabels={roleLabels}
                        availableRoles={availableRoles}
                        allLabel={roleFilterAllLabel}
                        applyLabel={t('store_settings:apply')}
                        clearLabel={t('store_settings:clear')}
                      />
                      <ColumnsPopover
                        columns={activeColumns}
                        visibleIds={activeVisible}
                        onChange={setActiveVisible}
                        buttonLabel={t('store_settings:columns_button_label')}
                        popoverTitle={t('store_settings:columns_popover_title')}
                        resolveLabel={resolveLabel}
                        tagSource={viewMode === 'users' ? 'employee' : 'user'}
                      />
                      <ExportButton
                        icon={<PrinterIcon size={5} color="gray.700" />}
                        buttonLabel={t('store_settings:print_button_label')}
                        accessibilityLabel={t('store_settings:print_button_label')}
                        currentViewLabel={t('store_settings:print_current_view')}
                        fullReportLabel={t('store_settings:print_full_report')}
                        onSelect={handlePrint}
                      />
                      <ExportButton
                        icon={<MsExcel width={20} height={20} />}
                        buttonLabel={t('store_settings:excel_button_label')}
                        accessibilityLabel={t('store_settings:excel_button_label')}
                        currentViewLabel={t('store_settings:print_current_view')}
                        fullReportLabel={t('store_settings:print_full_report')}
                        onSelect={handleExcel}
                      />
                    </HStack>
                  </HStack>
                }
              />
            </VStack>
          )}

          <PrintView
            isOpen={isPrintOpen}
            onClose={() => setIsPrintOpen(false)}
            members={printMembers}
            columns={printColumns}
            columnContext={columnContext}
            resolveLabel={resolveLabel}
            siteName={siteConfig?.name ?? ''}
            chainName={chainName}
            reportTitle={exportReportTitle()}
            filterDescription={printFilterDescription}
            brandLogoUrl={brandLogoUrl}
            customerName={customerName}
            address={address}
            storeInfoLabel={t('store_settings:report_store_information')}
            customerLabel={t('store_settings:report_customer')}
            storeLabel={t('store_settings:report_store')}
            addressLabel={t('store_settings:address')}
            employeeRecordsLabel={
              viewMode === 'users' ? t('store_settings:report_user_records_section') : t('store_settings:report_employee_records_section')
            }
            filtersLabel={t('store_settings:report_filters')}
            generatedAtLabel={t('store_settings:report_generated_at')}
            generatedByName={generatedByName}
            fileName={exportFileName(printScope)}
            emptyMessage={t('store_settings:team_members_empty')}
            closeLabel={t('store_settings:close')}
            printLabel={t('store_settings:print_button_label')}
          />

          <MemberProfileModal
            member={activeProfileMember}
            onClose={memberProfileModal.setHide}
            onMemberUpdated={updateCachedMember}
            columnContext={columnContext}
            vendorName={vendorName}
            siteName={siteConfig?.name ?? ''}
            customerName={customerName}
            address={address}
            chainName={chainName}
            brandLogoUrl={brandLogoUrl}
            generatedByName={generatedByName}
            storeCode={siteConfig?.stopId}
            canEditRoster={canEditRoster}
            canSetPrimary={canSetPrimary}
          />

          <AddEmployeeModal
            isOpen={isAddEmployeeOpen}
            onClose={() => setIsAddEmployeeOpen(false)}
            onCreate={async values => {
              const created = await createTeamMember.mutateAsync({
                siteId: siteConfig?.id,
                roleName: values.role,
                firstName: values.firstName,
                lastName: values.lastName,
                title: values.title || undefined,
                // No existing record to preserve a real year from — toIsoBirthDate
                // falls back to its fixed placeholder year (birthday is year-less
                // in this app; only month/day is ever collected or shown).
                birthDate: toIsoBirthDate(values.birthDate),
                hireDate: toIsoDate(values.hireDate),
                email: values.email || undefined,
                mobileNumber: values.mobileNumber || undefined,
                employeeNum: values.employeeNum || undefined,
                importSyncMode: values.importSyncMode as unknown as ImportSyncModeEnum,
              });
              insertCachedMember(created);
              return created;
            }}
            roleLabels={roleLabels}
            chainName={chainName}
            storeCode={siteConfig?.stopId}
          />
        </VStack>
      </View>
    </HStack>
  );
};

export default TeamManagementView;
